// src/admin/pages/EditProduct.tsx
import React, { useState, useEffect, useRef } from 'react';
import AdminLayout from '../components/Layout/AdminLayout';
import { useParams, useNavigate } from 'react-router-dom';
import { FaEdit, FaImage, FaArrowLeft, FaSave, FaTimes } from 'react-icons/fa';
import Notification from '../components/Notification/Notification';

interface Category {
  id: number;
  name: string;
  icon?: string;
  parentId: number | null;
  subCategories?: Category[];
}

interface CategoryType {
  id: number;
  name: string;
}

interface ProductData {
  id?: number;
  productName: string;
  productCode: string;
  description: string;
  price: number;
  discount: number;
  bulkDiscountPrice: number;
  taxRate: number;
  offer: string;
  stockQuantity: number;
  typeQuantity: number;
  measurementUnit: string;
  imageUrl: string;
  category: { id: number; name?: string };
  categoryType: { id: number; name?: string };
  status: string;
}

type PreviewImage = string | File;

const EditProduct: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  
  const [productData, setProductData] = useState<ProductData>({
    productName: '',
    productCode: '',
    description: '',
    price: 0,
    discount: 0,
    bulkDiscountPrice: 0,
    taxRate: 0,
    offer: '',
    stockQuantity: 0,
    typeQuantity: 0,
    measurementUnit: '',
    imageUrl: '',
    category: { id: 0 },
    categoryType: { id: 0 },
    status: 'active'
  });

  const [previewImages, setPreviewImages] = useState<PreviewImage[]>([]);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' | 'info'; } | null>(null);

  // --- Category & Organization States ---
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoryTypes, setCategoryTypes] = useState<CategoryType[]>([]);
  const [hierarchicalCategories, setHierarchicalCategories] = useState<Category[]>([]);
  const [isCategoryTreeOpen, setIsCategoryTreeOpen] = useState<boolean>(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<number>>(new Set());
  const [selectedCategoryName, setSelectedCategoryName] = useState<string>('Select Category...');
  const [availableMeasurementUnits, setAvailableMeasurementUnits] = useState<string[]>([]);
  const [loadingCategories, setLoadingCategories] = useState<boolean>(true);
  const [loadingCategoryTypes, setLoadingCategoryTypes] = useState<boolean>(true);

  // YAHAN FIX KIYA HAI: ProductList.tsx wala same image path use kiya hai
  const IMAGE_BASE_URL = "https://www.ayushvatika.com/images/products/";
  // Agar upar wala path kaam na kare, to isse uncomment karke try karna:
  // const IMAGE_BASE_URL = "http://localhost:8484/api/uploads/";

  const showNotification = (message: string, type: 'success' | 'error' | 'info') => {
    setNotification({ message, type });
  };

  // Build category tree
  const buildCategoryTree = (cats: Category[]) => {
    const categoriesMap = new Map<number, Category>();
    cats.forEach(cat => categoriesMap.set(cat.id, { ...cat, subCategories: [] }));

    const rootCategories: Category[] = [];
    cats.forEach(cat => {
      if (cat.parentId === null || cat.parentId === undefined) {
        rootCategories.push(categoriesMap.get(cat.id)!);
      } else {
        const parent = categoriesMap.get(cat.parentId);
        if (parent) {
          parent.subCategories?.push(categoriesMap.get(cat.id)!);
        } else {
          rootCategories.push(categoriesMap.get(cat.id)!);
        }
      }
    });
    return rootCategories;
  };

  // Fetch Categories & Types
  useEffect(() => {
    const fetchCategoriesAndTypes = async () => {
      try {
        const [catRes, typeRes] = await Promise.all([
          fetch('https://www.ayushvatika.com/api/categories'),
          fetch('https://www.ayushvatika.com/api/category-types')
        ]);
        
        if (catRes.ok) {
          const catsData: Category[] = await catRes.json();
          setCategories(catsData);
          setHierarchicalCategories(buildCategoryTree(catsData));
        }
        if (typeRes.ok) {
          const typesData: CategoryType[] = await typeRes.json();
          setCategoryTypes(typesData);
        }
      } catch (err) {
        console.error("Failed to fetch taxonomy data", err);
      } finally {
        setLoadingCategories(false);
        setLoadingCategoryTypes(false);
      }
    };
    fetchCategoriesAndTypes();
  }, []);

  // Set Measurement Units based on Category Type
  useEffect(() => {
    if (!productData.categoryType.id) {
      setAvailableMeasurementUnits([]);
      return;
    }
    const selectedType = categoryTypes.find(type => type.id === productData.categoryType.id);
    if (selectedType) {
      const typeName = selectedType.name.toLowerCase().trim();
      let units: string[] = [];
      if (typeName === 'liquid') units = ['ml', 'litre'];
      else if (typeName === 'capsule' || typeName === 'tablets' || typeName === 'tablet') units = ['pieces', 'strips'];
      else if (typeName === 'powder') units = ['gm', 'kg'];
      else units = ['pieces', 'kg', 'gm', 'ml', 'litre']; 
      setAvailableMeasurementUnits(units);
    }
  }, [productData.categoryType.id, categoryTypes]);

  // Sync Category Name when Product Data or Categories change
  useEffect(() => {
    if (productData.category?.id && categories.length > 0) {
      const cat = categories.find(c => c.id === productData.category.id);
      if (cat) setSelectedCategoryName(cat.name);
    }
  }, [productData.category?.id, categories]);

  // Fetch Product Data
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await fetch(`https://www.ayushvatika.com/api/products/${id}`);
        if (!response.ok) throw new Error('Failed to fetch product data');
        const data: ProductData = await response.json();
        setProductData(data);

        if (data.imageUrl && data.imageUrl.trim() !== '') {
          const existingUrls = data.imageUrl.split(',').map(url => url.trim()).filter(url => url !== '');
          setPreviewImages(existingUrls);
        } else {
          setPreviewImages([]);
        }
      } catch (err: any) {
        showNotification(`Error loading product: ${err.message}`, 'error');
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchProduct();
  }, [id, navigate]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setProductData(prev => ({
      ...prev,
      [name]: name === 'category' || name === 'categoryType' ? { id: parseInt(value) || 0 } : value
    }));
  };

  const compressImage = async (file: File): Promise<File> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target?.result as string;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx?.drawImage(img, 0, 0, img.width, img.height);
          canvas.toBlob((blob) => {
            if (blob) {
              const newFile = new File([blob], file.name.replace(/\.[^/.]+$/, "") + ".jpg", { type: 'image/jpeg', lastModified: Date.now() });
              resolve(newFile);
            } else resolve(file);
          }, 'image/jpeg', 0.85); 
        };
        img.onerror = (error) => reject(error);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const handleImageBrowse = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (previewImages.length + files.length > 5) {
      showNotification('Total images cannot exceed 5.', 'info');
      return;
    }
    try {
      const compressedFiles = await Promise.all(files.map(file => compressImage(file)));
      setPreviewImages(prev => [...prev, ...compressedFiles]);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      showNotification('Error compressing images.', 'error');
    }
  };

  const handleImageDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;
    if (previewImages.length + files.length > 5) {
      showNotification('Total images cannot exceed 5.', 'info');
      return;
    }
    try {
      const compressedFiles = await Promise.all(files.map(file => compressImage(file)));
      setPreviewImages(prev => [...prev, ...compressedFiles]);
    } catch (err) {
      showNotification('Error compressing images.', 'error');
    }
  };

  const removeImage = (index: number) => {
    setPreviewImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSaveChanges = async () => {
    if (!productData.productName || !productData.category.id || !productData.categoryType.id || !productData.measurementUnit) {
      showNotification('Please fill in all required fields (Name, Category, Category Type, Unit).', 'error');
      return;
    }

    setSaving(true);
    try {
      const existingUrlsUsingStrings = previewImages.filter(img => typeof img === 'string') as string[];
      const newFilesToUpload = previewImages.filter(img => img instanceof File) as File[];
      let newlyUploadedUrls: string[] = [];

      if (newFilesToUpload.length > 0) {
        for (const file of newFilesToUpload) {
          const formData = new FormData();
          formData.append('file', file);
          const uploadResponse = await fetch('https://www.ayushvatika.com/api/products/upload-image', {
            method: 'POST', body: formData,
          });
          if (!uploadResponse.ok) throw new Error('Failed to upload new images.');
          const uploadedFileName = await uploadResponse.text();
          newlyUploadedUrls.push(uploadedFileName);
        }
      }

      const finalUrlList = [...existingUrlsUsingStrings, ...newlyUploadedUrls];
      const finalImageUrlString = finalUrlList.length > 0 ? finalUrlList.join(',') : null;

      const payload = {
        ...productData,
        price: Number(productData.price),
        discount: Number(productData.discount),
        bulkDiscountPrice: Number(productData.bulkDiscountPrice),
        taxRate: Number(productData.taxRate),
        stockQuantity: Number(productData.stockQuantity),
        typeQuantity: Number(productData.typeQuantity),
        imageUrl: finalImageUrlString,
      };

      const response = await fetch(`https://www.ayushvatika.com/api/products/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
         const errText = await response.text();
         throw new Error(`Failed to update product: ${errText}`);
      }

      showNotification('Product updated successfully!', 'success');
      setTimeout(() => navigate('/admin/product/list'), 1500);
    } catch (err: any) {
      console.error("Error saving changes:", err);
      showNotification(`Error saving changes: ${err.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const renderCategoryTree = (categories: Category[], level: number = 0): React.ReactNode => {
    return categories.map(category => {
      const hasChildren = category.subCategories && category.subCategories.length > 0;
      const isExpanded = expandedCategories.has(category.id);
      return (
        <div key={category.id} style={{ paddingLeft: `${level * 16}px` }}>
          <div
            className={`flex items-center cursor-pointer px-2 py-2 rounded transition-colors ${
              productData.category.id === category.id 
              ? 'bg-blue-100 text-blue-700 font-bold dark:bg-blue-900/30 dark:text-blue-400' 
              : 'text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-600'
            }`}
            onClick={() => {
              setProductData(prev => ({ ...prev, category: { id: category.id } }));
              setSelectedCategoryName(category.name);
              setIsCategoryTreeOpen(false);
            }}
          >
            {hasChildren && (
              <button
                type="button"
                className="mr-2 text-xs text-gray-500 dark:text-gray-400 focus:outline-none"
                onClick={e => {
                  e.stopPropagation();
                  setExpandedCategories(prev => {
                    const next = new Set(prev);
                    if (next.has(category.id)) next.delete(category.id);
                    else next.add(category.id);
                    return next;
                  });
                }}
              >
                {isExpanded ? '▼' : '▶'}
              </button>
            )}
            <span className="flex items-center gap-2">
              {category.icon && <span className="inline-block w-4 h-4 fill-current" dangerouslySetInnerHTML={{ __html: category.icon }} />}
              {category.name}
            </span>
          </div>
          {hasChildren && isExpanded && (
            <div className="border-l border-gray-300 dark:border-gray-600 ml-2">
              {renderCategoryTree(category.subCategories!, level + 1)}
            </div>
          )}
        </div>
      );
    });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-8 flex justify-center items-center h-screen text-gray-500 font-medium text-lg">
          Loading product data...
        </div>
      </AdminLayout>
    );
  }

  const inputStyle = "w-full p-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all";

  return (
    <AdminLayout>
      <div className="min-h-screen p-6 md:p-8 font-sans transition-colors bg-gray-50 dark:bg-gray-900">
        
        {/* Header & Actions */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div className="flex items-center">
            <button onClick={() => navigate(-1)} className="mr-4 p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-gray-600 dark:text-gray-300">
               <FaArrowLeft />
            </button>
            <h1 className="text-3xl font-bold flex items-center text-gray-800 dark:text-white">
              <FaEdit className="mr-3 text-blue-600 dark:text-blue-500" /> Edit Product
            </h1>
          </div>
          <div className="flex gap-3">
             <button onClick={() => navigate(-1)} disabled={saving} className="px-6 py-2.5 rounded-lg border font-semibold shadow-sm transition-colors border-gray-300 text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 flex items-center">
               <FaTimes className="mr-2" /> Cancel
             </button>
             <button onClick={handleSaveChanges} disabled={saving} className={`px-6 py-2.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 hover:shadow-md transition-all font-semibold shadow-sm flex items-center ${saving ? 'opacity-70 cursor-not-allowed' : ''}`}>
               <FaSave className="mr-2" /> {saving ? 'Saving...' : 'Save Changes'}
             </button>
          </div>
        </div>

        {notification && (
          <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column: Basic Info & Pricing */}
          <div className="lg:col-span-2 space-y-8">
             {/* Basic Info Card */}
             <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <h2 className="text-lg font-semibold mb-6 text-gray-900 dark:text-white pb-2 border-b border-gray-100 dark:border-gray-700">Basic Information</h2>
                <div className="space-y-5">
                   <div>
                      <label htmlFor="productName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Product Name</label>
                      <input type="text" id="productName" name="productName" value={productData.productName} onChange={handleInputChange} className={inputStyle} />
                   </div>
                   <div>
                      <label htmlFor="productCode" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Product Code</label>
                      <input type="text" id="productCode" name="productCode" value={productData.productCode || ''} onChange={handleInputChange} className={inputStyle} placeholder="e.g. ALJC001" />
                   </div>
                   <div>
                      <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                      <textarea id="description" name="description" rows={4} value={productData.description} onChange={handleInputChange} className={`${inputStyle} resize-y`}></textarea>
                   </div>
                </div>
             </div>

             {/* Pricing Card */}
             <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <h2 className="text-lg font-semibold mb-6 text-gray-900 dark:text-white pb-2 border-b border-gray-100 dark:border-gray-700">Pricing & Stock</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                   <div>
                      <label htmlFor="price" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Price (₹)</label>
                      <input type="number" id="price" name="price" value={productData.price} onChange={handleInputChange} className={inputStyle} min="0" step="0.01" />
                   </div>
                   <div>
                      <label htmlFor="discount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Discount (%)</label>
                      <input type="number" id="discount" name="discount" value={productData.discount} onChange={handleInputChange} className={inputStyle} min="0" max="100" step="0.01" />
                   </div>
                   <div>
                      <label htmlFor="bulkDiscountPrice" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Bulk Price (₹)</label>
                      <input type="number" id="bulkDiscountPrice" name="bulkDiscountPrice" value={productData.bulkDiscountPrice || 0} onChange={handleInputChange} className={inputStyle} min="0" step="0.01" />
                   </div>
                   <div>
                      <label htmlFor="taxRate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tax Rate (%)</label>
                      <input type="number" id="taxRate" name="taxRate" value={productData.taxRate || 0} onChange={handleInputChange} className={inputStyle} min="0" step="0.01" />
                   </div>
                   <div>
                      <label htmlFor="offer" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Offer</label>
                      <input type="text" id="offer" name="offer" value={productData.offer || ''} onChange={handleInputChange} className={inputStyle} placeholder="e.g. Buy 1 Get 1" />
                   </div>
                   <div>
                      <label htmlFor="stockQuantity" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Stock Quantity</label>
                      <input type="number" id="stockQuantity" name="stockQuantity" value={productData.stockQuantity} onChange={handleInputChange} className={inputStyle} min="0" />
                   </div>
                    <div className="md:col-span-2">
                        <label htmlFor="status" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                        <select id="status" name="status" value={productData.status} onChange={handleInputChange} className={inputStyle}>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                            <option value="out_of_stock">Out of Stock</option>
                        </select>
                    </div>
                </div>
             </div>
          </div>

          {/* Right Column: Images & Organization */}
          <div className="lg:col-span-1 space-y-8">
             {/* Image Upload Card */}
             <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <h2 className="text-lg font-semibold mb-6 text-gray-900 dark:text-white pb-2 border-b border-gray-100 dark:border-gray-700">Product Images</h2>
                
                <div
                   className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-2xl p-8 text-center cursor-pointer hover:border-blue-500 dark:hover:border-blue-400 transition-colors bg-gray-50 dark:bg-gray-700/30"
                   onDragOver={(e) => e.preventDefault()}
                   onDrop={handleImageDrop}
                   onClick={() => fileInputRef.current?.click()}
                >
                   <FaImage className="mx-auto text-4xl text-gray-400 dark:text-gray-500 mb-4" />
                   <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">Drag & drop images here, or <span className="text-blue-600 dark:text-blue-400 font-medium">click to browse</span></p>
                   <p className="text-xs text-gray-500 dark:text-gray-400">Supports JPG, PNG, GIF (Max 5 images)</p>
                   <input
                      type="file"
                      ref={fileInputRef}
                      className="hidden"
                      multiple
                      accept="image/jpeg,image/png,image/gif"
                      onChange={handleImageBrowse}
                   />
                </div>

                <div className="mt-6 space-y-3">
                   {previewImages.map((image, index) => {
                      // YAHAN FIX KIYA HAI: encodeURIComponent lagaya hai taaki "banner1 - Copy.jpg" jaise naam chal jayein
                      let src = typeof image === 'string' ? `${IMAGE_BASE_URL}${encodeURIComponent(image)}` : URL.createObjectURL(image);
                      return (
                        <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200 dark:border-gray-600 group">
                           <div className="flex items-center space-x-4">
                              <img
                                 src={src}
                                 alt={`Preview ${index}`}
                                 className="w-16 h-16 object-cover rounded-lg border border-gray-300 dark:border-gray-500"
                                 onError={(e) => {
                                     // Default fallback in case image still doesn't load
                                     if (typeof image === 'string') (e.target as HTMLImageElement).src = 'https://via.placeholder.com/150?text=Image+Not+Found';
                                 }}
                              />
                              <span className="text-sm text-gray-600 dark:text-gray-300 truncate max-w-[150px]">
                                 {typeof image === 'string' ? image : image.name}
                              </span>
                           </div>
                           <button onClick={() => removeImage(index)} className="p-2 text-gray-400 hover:text-red-500 transition-colors rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 opacity-0 group-hover:opacity-100 focus:opacity-100" title="Remove image">
                              <FaTimes />
                           </button>
                        </div>
                      );
                   })}
                </div>
             </div>

             {/* Organization Card */}
             <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-lg font-semibold mb-6 text-gray-900 dark:text-white pb-2 border-b border-gray-100 dark:border-gray-700">Organization</h2>
              
              <div className="mb-5 relative">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Category</label>
                <div
                  className={`${inputStyle} cursor-pointer flex justify-between items-center`}
                  onClick={() => setIsCategoryTreeOpen(!isCategoryTreeOpen)}
                >
                  <span>{selectedCategoryName}</span>
                  <span className="text-xs text-gray-400">▼</span>
                </div>
                {isCategoryTreeOpen && (
                  <div className="absolute z-10 w-full mt-1 rounded-xl shadow-2xl max-h-60 overflow-y-auto bg-white border border-gray-200 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200">
                    {loadingCategories ? (
                      <p className="p-3 text-sm text-gray-500">Loading categories...</p>
                    ) : (
                      <div className="py-1">{renderCategoryTree(hierarchicalCategories)}</div>
                    )}
                  </div>
                )}
              </div>

              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Category Type</label>
                <select
                  name="categoryType"
                  className={inputStyle}
                  value={productData.categoryType.id || ''}
                  onChange={handleInputChange}
                  disabled={loadingCategoryTypes}
                >
                  <option value="">{loadingCategoryTypes ? 'Loading...' : 'Select Category Type...'}</option>
                  {categoryTypes.map((type) => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </select>
              </div>

              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Unit of Measurement</label>
                <div className="flex">
                  <input
                    type="number"
                    name="typeQuantity"
                    placeholder="e.g., 500"
                    className={`${inputStyle} rounded-r-none border-r-0`}
                    style={{width: '65%'}}
                    value={productData.typeQuantity}
                    onChange={handleInputChange}
                  />
                  <select
                    name="measurementUnit"
                    className={`${inputStyle} rounded-l-none border-l bg-gray-100 border-l-gray-300 dark:bg-gray-600 dark:border-l-gray-600`}
                    style={{width: '35%'}}
                    value={productData.measurementUnit}
                    onChange={handleInputChange}
                    disabled={!productData.categoryType.id}
                  >
                    <option value="">Unit</option>
                    {availableMeasurementUnits.map((unit) => (
                      <option key={unit} value={unit}>{unit}</option>
                    ))}
                  </select>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default EditProduct;